import React, {useState} from "react";
import {Box} from "@mui/material";
import NavBar from "./components/NavBar";
import AuthDialog from "./components/AuthDialog";
import Landing from "./pages/Landing";
import {Invitation} from "./pages/ForgotPassword";
import {clearSession, isSignedIn} from "./util/SessionContext";

// The Supplier portal. One page under a title bar; signing in happens in a dialog over it, which
// opens by itself for a signed-out visitor.
//
// No router: there is one destination, and the authentication steps are stages of a dialog rather
// than places you can be. A URL per step would only invite someone to deep-link into the middle of a
// password reset.

// An invitation arrives as ?invite=<key>.<code>, the two halves the reset endpoint already expects.
// It is read ONCE, at mount, and stripped from the address bar immediately: the key is single use,
// so leaving it in the URL only invites a refresh to replay a spent invitation and read as broken.
function readInvitation(): Invitation | undefined {
    const value = new URLSearchParams(window.location.search).get("invite");
    if (!value) return undefined;

    const separator = value.indexOf(".");
    if (separator <= 0 || separator === value.length - 1) return undefined;

    window.history.replaceState({}, "", window.location.pathname);

    return {key: value.slice(0, separator), code: value.slice(separator + 1)};
}

export default function App() {
    const [invitation] = useState<Invitation | undefined>(readInvitation);

    // An invitation names one person, who is not necessarily whoever this browser is already signed
    // in as -- a shared machine, or a second contact at the same company. Following the link
    // therefore ends the current session rather than being swallowed by it, which is also what stops
    // the link doing nothing at all for someone who happens to be signed in.
    const [signedIn, setSignedIn] = useState<boolean>(() => {
        if (invitation) {
            clearSession();
            return false;
        }
        return isSignedIn();
    });
    const [authOpen, setAuthOpen] = useState<boolean>(() => !!invitation || !isSignedIn());

    const signOut = () => {
        clearSession();
        setSignedIn(false);
        setAuthOpen(true);
    };

    return (
        <Box sx={{height: "100%", display: "flex", flexDirection: "column"}}>
            <NavBar signedIn={signedIn} onSignIn={() => setAuthOpen(true)} onSignOut={signOut}/>

            <Box sx={{flex: 1, minHeight: 0, overflow: "auto"}}>
                <Landing signedIn={signedIn}/>
            </Box>

            <AuthDialog open={authOpen && !signedIn}
                        onClose={() => setAuthOpen(false)}
                        onSignedIn={() => { setSignedIn(true); setAuthOpen(false); }}
                        invitation={invitation}/>
        </Box>
    );
}
